import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import {
  ChartLineUp,
  Cow,
  Users,
  FileText,
  Bell,
  Clock,
  SignOut,
  List,
  X,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import InstallPWA from "@/components/InstallPWA";
import api from "@/lib/api";

const NAV = [
  { to: "/dashboard", label: "Painel", icon: ChartLineUp, roles: null },
  { to: "/animais", label: "Animais", icon: Cow, roles: null },
  { to: "/alertas", label: "Alertas", icon: Bell, roles: null, badge: true },
  { to: "/relatorios", label: "Relatórios", icon: FileText, roles: null },
  { to: "/usuarios", label: "Usuários", icon: Users, roles: ["admin"] },
  { to: "/auditoria", label: "Auditoria", icon: Clock, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    api.get("/alertas").then((r) => setAlertCount(r.data?.total || 0)).catch(() => {});
  }, []);

  const items = NAV.filter((n) => !n.roles || n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[#F6F5F2] text-[#1A1C19] font-body">
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-[#1A1C19]/50 md:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`fixed z-40 inset-y-0 left-0 w-64 bg-white border-r border-[#E2E0D8] transform transition-transform md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 border-b border-[#E2E0D8] flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-[#2A5A3B] flex items-center justify-center">
            <Cow size={22} color="#fff" weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-black tracking-tight text-lg leading-none">
              CEDUP Leite
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4E544B] mt-1">
              Gestão de rebanho
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-[#2A5A3B] text-white"
                    : "text-[#1A1C19] hover:bg-[#F6F5F2]"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span className="flex-1">{n.label}</span>
              {n.badge && alertCount > 0 && (
                <span
                  data-testid="nav-alert-badge"
                  className="text-[10px] font-bold bg-[#C05746] text-white rounded-full px-2 py-0.5"
                >
                  {alertCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E2E0D8] bg-white">
          <div className="mb-3">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-[#4E544B]">
              {ROLE_LABELS[user.role]}
            </div>
          </div>
          <NavLink
            to="/trocar-senha"
            data-testid="link-change-password"
            className="w-full block mb-2 text-center text-xs font-bold text-[#2A5A3B] hover:underline"
          >
            Trocar minha senha
          </NavLink>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              nav("/login");
            }}
            className="w-full flex items-center justify-center gap-2 border border-[#C05746] text-[#C05746] text-sm font-bold py-2 rounded-sm hover:bg-[#FDEDED] transition-colors"
          >
            <SignOut size={16} weight="bold" /> Sair
          </button>
        </div>
      </aside>

      {/* Topbar mobile */}
      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-[#E2E0D8] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-[#2A5A3B] flex items-center justify-center">
            <Cow size={18} color="#fff" weight="duotone" />
          </div>
          <span className="font-heading font-black">CEDUP Leite</span>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          aria-expanded={open}
          className="p-2 rounded-sm border border-[#E2E0D8]"
        >
          {open ? <X size={18} /> : <List size={18} />}
        </button>
      </div>

      <main className="md:ml-64 p-4 md:p-8 lg:p-10 pb-24 md:pb-10">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E2E0D8] flex justify-around"
      >
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`bottomnav-${n.to.slice(1)}`}
            className={({ isActive }) =>
              `relative flex-1 flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                isActive
                  ? "text-[#2A5A3B]"
                  : "text-[#4E544B]"
              }`
            }
          >
            <n.icon size={22} weight="duotone" />
            {n.label}
            {n.badge && alertCount > 0 && (
              <span className="absolute top-1 right-4 md:right-6 text-[9px] font-bold bg-[#C05746] text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                {alertCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <InstallPWA />
      <Toaster position="top-right" richColors />
    </div>
  );
}
